// Customer preview — renders the REAL portal experience (same components) from a draft template posted by the Template Builder.
// Edit mode: clicks on headings / questions / document cards are intercepted and reported back to the builder.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { PortalExperience } from './OnboardingPortal';
import { fieldVisible, valuesFromAnswers, buildJourney, ctxForSlot, stepKey } from '../components/onboarding/rules';
import '../components/builder/preview-edit.css';

const API = process.env.REACT_APP_BACKEND_URL;

function buildSections(template, answers) {
  const secs = (template.sections || []).filter((s) => s.visibility !== 'hidden').map((s) => ({ ...s, fields: (s.fields || []).filter((f) => f.visibility !== 'hidden').map((f) => ({ ...f, required: f.visibility ? f.visibility === 'required' : f.required !== false })) }));
  const joint = valuesFromAnswers(answers.objective).applicant_count === 'joint';
  const steps = buildJourney(secs, joint).map((s) => ({ ...s, answers: answers[s.step_key] || {} }));
  const ctxBy = { 1: ctxForSlot(steps, 1), 2: ctxForSlot(steps, 2) };
  return steps.map((s, i) => {
    const vals = valuesFromAnswers(s.answers);
    const missing = s.fields.filter((f) => f.required && fieldVisible(f, ctxBy[s.applicant_slot], vals) && (vals[f.key] === undefined || vals[f.key] === null || vals[f.key] === '')).map((f) => f.key);
    const confirmed = !!answers.__confirmed?.[s.step_key];
    const answeredAny = Object.keys(vals).length > 0;
    const state = confirmed && !missing.length ? 'complete' : answeredAny ? 'in_progress' : 'todo';
    return { ...s, order: i, state, missing, minutes: s.minutes || 3, shared_readonly: false };
  });
}

// Scenario answers ({section: {key: value}}) → stored answer shape
const answersFromScenario = (sc) => Object.fromEntries(Object.entries(sc || {}).map(([sec, vals]) => [sec, Object.fromEntries(Object.entries(vals).map(([k, v]) => [k, { value: v, source: 'client', status: 'entered' }]))]));

export default function PortalPreview() {
  const [msg, setMsg] = useState(null);
  const [answers, setAnswers] = useState({});
  const [documents, setDocuments] = useState([]);
  const [consent, setConsent] = useState(null);
  const [completed, setCompleted] = useState(null);
  const [thankYou, setThankYou] = useState(null);
  const [forced, setForced] = useState(null);
  const scenarioRef = useRef(null);
  const docsRef = useRef([]);
  docsRef.current = documents;
  useEffect(() => { axios.get(`${API}/api/onboarding/thank-you-settings`).then((r) => setThankYou(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const onMsg = (e) => {
      const d = e.data || {};
      if (d.type === 'ff-preview') {
        setMsg(d);
        const sig = d.scenario ? `${d.scenario.key}:${d.scenario.nonce}` : null;
        if (sig && sig !== scenarioRef.current) { scenarioRef.current = sig; setAnswers(answersFromScenario(d.scenario.answers)); setConsent(null); setCompleted(null); }
        if (d.view) setForced(`${d.view.name}::${d.view.key || ''}::${d.view.nonce ?? ''}`);
        else if (d.highlight?.section) setForced(`${d.highlight.section}::${d.highlight.key}::${d.highlight.nonce || ''}`);
      }
      if (d.type === 'ff-preview-reset') { setAnswers({}); setConsent(null); setDocuments([]); setCompleted(null); setForced(`home::::${Date.now()}`); }
      if (d.type === 'ff-preview-view') setForced(`${d.view}::::${Date.now()}`);
    };
    window.addEventListener('message', onMsg);
    window.parent?.postMessage({ type: 'ff-preview-ready' }, '*');
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const template = msg?.template;
  const editing = !!msg?.edit;
  const sections = useMemo(() => (template ? buildSections(template, answers) : []), [template, answers]);

  // Documents come from the real rules engine so the preview checklist matches production exactly
  useEffect(() => {
    if (!template || !msg?.token) return undefined;
    const joint = sections.some((s) => s.applicant_slot === 2);
    const t = setTimeout(async () => {
      try {
        const hdr = { headers: { Authorization: `Bearer ${msg.token}` } };
        const run = (slot) => axios.post(`${API}/api/fact-find/templates/${template.id}/simulate`, msg.docsAll ? { all: true, document_rules: template.document_rules } : { ctx: ctxForSlot(sections, slot), document_rules: template.document_rules }, hdr).then((r) => r.data.documents || []);
        const [d1, d2] = await Promise.all([run(1), joint && !msg.docsAll ? run(2) : Promise.resolve([])]);
        const rows = [...d1.map((d) => ({ ...d, applicant_id: d.scope === 'shared' ? null : 'me', applicant_label: null })), ...d2.filter((d) => d.scope !== 'shared').map((d) => ({ ...d, applicant_id: 'co', applicant_label: 'Applicant 2 · Second applicant' }))];
        setDocuments((prev) => rows.map((d, i) => { const old = prev.find((p) => p.item_key === d.item_key && p.applicant_id === d.applicant_id); return { id: `prev-${d.applicant_id || 'shared'}-${d.item_key}-${i}`, status: 'requested', files: [], required: d.required !== false, ...d, ...(old ? { status: old.status, files: old.files } : {}) }; }));
      } catch { /* preview only */ }
    }, 400);
    return () => clearTimeout(t);
  }, [sections, template, msg]);

  // ── Edit mode: intercept clicks and report what was clicked ──
  useEffect(() => {
    if (!editing || !template) return undefined;
    const outermost = (el, test) => { let found = null; let n = el; while (n && n !== document.body) { if (test(n)) found = n; n = n.parentElement; } return found; };
    const onClick = (e) => {
      const el = e.target;
      const docEl = outermost(el, (n) => (n.getAttribute?.('data-testid') || '').startsWith('ob-doc-prev-'));
      if (docEl) {
        const m = docEl.getAttribute('data-testid').match(/^ob-doc-(prev-.+)$/);
        const doc = m ? docsRef.current.find((d) => d.id === m[1]) : null;
        if (doc?.triggered_by?.rule_id) { e.preventDefault(); e.stopPropagation(); window.parent?.postMessage({ type: 'ff-edit-select', sel: { kind: 'document', id: doc.triggered_by.rule_id } }, '*'); }
        return;
      }
      const qEl = outermost(el, (n) => n.id && n.id.startsWith('ob-q-'));
      const forcedSection = (forced || '').split('::')[0].replace(/@2$/, '');
      if (qEl) {
        const key = qEl.id.slice(5);
        const sec = template.sections.find((s) => s.key === forcedSection && (s.fields || []).some((f) => f.key === key)) || template.sections.find((s) => (s.fields || []).some((f) => f.key === key));
        if (sec) { e.preventDefault(); e.stopPropagation(); window.parent?.postMessage({ type: 'ff-edit-select', sel: { kind: 'question', section: sec.key, key } }, '*'); }
        return;
      }
      if (el.closest?.('[data-testid="ob-section-heading"], [data-testid="ob-section-intro"]')) {
        const sec = template.sections.find((s) => s.key === forcedSection) || null;
        if (sec) { e.preventDefault(); e.stopPropagation(); window.parent?.postMessage({ type: 'ff-edit-select', sel: { kind: 'section', section: sec.key } }, '*'); }
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [editing, template, forced]);

  // Selected document card ring
  useEffect(() => {
    document.querySelectorAll('.ff-selected').forEach((n) => n.classList.remove('ff-selected'));
    const rid = msg?.highlight?.rule; if (!rid) return;
    const doc = documents.find((d) => d.triggered_by?.rule_id === rid);
    const el = doc && document.querySelector(`[data-testid="ob-doc-${doc.id}"]`);
    if (el) { el.classList.add('ff-selected'); el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
  }, [msg?.highlight, documents]);

  const api = useMemo(() => ({
    saveSection: async (key, values, confirm, slot = 1) => {
      const sk = stepKey(key, slot);
      setAnswers((a) => ({ ...a, [sk]: Object.fromEntries(Object.entries(values).map(([k, v]) => [k, { value: v, source: 'client', status: 'entered' }])), __confirmed: { ...(a.__confirmed || {}), [sk]: confirm || a.__confirmed?.[sk] } }));
      return {};
    },
    shared: async () => {},
    upload: async (docId, file) => { setDocuments((ds) => ds.map((d) => (d.id === docId ? { ...d, status: 'uploaded', files: [...(d.files || []), { original_filename: file.name, size: file.size, uploaded_at: new Date().toISOString() }] } : d))); },
    dontHave: async (docId, reason) => { setDocuments((ds) => ds.map((d) => (d.id === docId ? { ...d, dont_have: { reason } } : d))); },
    consent: async (items, pack_version) => setConsent({ pack_version, acknowledged: items }),
    setPath: async () => {},
    addCoApplicant: async (body) => ({ first_name: body.first_name }),
    message: async () => {},
    submit: async () => { setCompleted(new Date().toISOString()); return {}; },
    bankStatementsClick: async () => {},
    removeFile: async (docId, idx) => { setDocuments((ds) => ds.map((d) => (d.id === docId ? { ...d, files: (d.files || []).filter((_, i) => i !== idx), status: (d.files || []).length > 1 ? d.status : 'requested' } : d))); },
  }), []);

  if (!template) return <div className="min-h-screen bg-[#2E333B] flex items-center justify-center text-white/70 text-sm" data-testid="ob-preview-waiting">Waiting for the template…</div>;
  const objective = sections.find((s) => s.key === 'objective');
  const joint = valuesFromAnswers(objective?.answers).applicant_count === 'joint';
  const payload = {
    application: { objective_label: template.name, reference: 'PREVIEW', broker_name: 'Your broker', path: null, is_joint: false, co_applicants: [], can_add_co_applicant: false, joint, co_applicant: joint ? { first_name: valuesFromAnswers(answers['personal@2']).first_name || 'Applicant 2' } : null },
    applicant: { id: 'me', first_name: msg.first_name || 'Alex', completed_at: completed },
    bank_statements: { status: null }, thank_you: thankYou || { heading: "You're Almost Done!", reviews: [] }, docs_complete: !documents.some((d) => d.required && d.status === 'requested'),
    sections, documents, consent_done: !!consent, consents: consent, disclosure_pack: msg.disclosure_pack || { version: 'preview', items: [] },
    esign: { tasks: [], awaiting_from_broker: (template.esign_docs || []).map((d) => d.name || d) },
    progress: { minutes_remaining: sections.filter((s) => s.state !== 'complete').reduce((n, s) => n + (s.minutes || 3), 0) },
  };
  return <div data-testid="ob-preview-root" data-preview-mode={msg.mode} className={editing ? 'ff-edit' : ''}><PortalExperience payload={payload} api={api} load={() => {}} setPayload={() => {}} preview forceView={forced} highlightKey={msg.highlight?.key || null} /></div>;
}
